import { TEXT_PAD, measureLineWidth, measuredTextHeight, wrapTextLines } from './drawObjects';
import { ensureFontsFor } from './fonts';
import { makeText, makeWhiteout } from './objects';
import {
    measureInkBounds,
    renderRegionCanvas,
    sampleBackgroundColor,
    sampleTextColor,
} from './pdfEngine';

/**
 * Turning a paragraph of the PDF into something you can type in.
 *
 * Two objects come out: a text box carrying the paragraph's words, and a patch
 * of the page colour that stands in for the original underneath it. The
 * exporter deletes the source operators inside `replaces` and throws the patch
 * away where that works; the canvas renders a copy with the same deletion
 * applied, so the two agree about what is on the page.
 *
 * Font family, weight, slant, size, line pitch, alignment and ink colour are
 * all read back from the PDF, so the replacement reflows and re-justifies like
 * the paragraph it replaces instead of looking pasted on. None of that is
 * React, which is why it lives here: the app's canvas and the site's cut-down
 * version of this tool both call it rather than keeping two copies of some very
 * particular arithmetic.
 *
 * @param {object} block A text block from `readTextBlocks`.
 * @param {{ pdf: object, pageNumber: number|null, pageW: number, pageH: number,
 *   baseCanvas: HTMLCanvasElement|null, defaults: object }} context
 *   `baseCanvas` is the already-rendered page, used to sample colours when the
 *   high-resolution probe cannot be taken. `defaults` is the text style a new
 *   box would otherwise get.
 * @returns {Promise<{ box: object, patch: object }|null>}
 */
/** How long the colour probe may hold up opening a paragraph for editing. */
const PROBE_TIMEOUT_MS = 1500;

export async function buildReplacementBox(block, {
    pdf,
    pageNumber,
    pageW,
    pageH,
    baseCanvas = null,
    defaults,
}) {
    if (!block?.lines?.length) return null;

    const clampRect = (rect) => ({
        ...rect,
        x: Math.max(0, Math.min(pageW - 4, rect.x)),
        y: Math.max(0, Math.min(pageH - 4, rect.y)),
        w: Math.max(2, Math.min(pageW - rect.x, rect.w)),
        h: Math.max(2, Math.min(pageH - rect.y, rect.h)),
    });

    const style = block.style || {};
    const bold = !!style.bold;
    const italic = !!style.italic;

    // Calibrate the size against the original text instead of trusting the text
    // matrix alone: a substitute family is never metrically identical to the
    // embedded one, so without this the replacement renders at a different
    // width, re-wraps, and visibly shrinks the paragraph.
    //
    // Measure a single contiguous run, preferring one without spaces. A whole
    // line is the wrong ruler — justification and tab-spread rows stretch the
    // gaps between words, which would inflate the size.
    //
    // When every run has spaces in it (a PDF that writes whole lines as one
    // run), take the SMALLEST ratio over them: justification only ever
    // stretches the gaps, so a justified line always measures wide and the
    // paragraph's unjustified last line is the honest one. Taking the widest
    // run there inflated the size by the justification slack (1.5% on a
    // typical handbook paragraph — the last line came back 3pt long).
    const items = block.lines.flatMap((l) => l.items || []);
    const usable = items.filter((it) => it.width > 1 && it.str.trim().length >= 3);
    const tight = usable.filter((it) => !/\s/.test(it.str.trim()));
    const probe = (tight.length ? tight : usable).sort((a, b) => b.width - a.width)[0];
    const ratioFor = (candidate) => {
        if (!probe) return 1;
        const ratioOf = (it) => {
            const width = measureLineWidth({ ...candidate, fontSize: block.fontSize }, it.str);
            return width > 1 ? it.width / width : null;
        };
        if (tight.length) return ratioOf(probe) ?? 1;
        const ratios = usable.map(ratioOf).filter((r) => r != null);
        return ratios.length ? Math.min(...ratios) : 1;
    };

    // Prefer the document's own font over the nearest catalogue family —
    // otherwise editing a paragraph visibly restyles it, which is what a user
    // notices first. It is adopted only if it *measures* like the original:
    // pdf.js maps a symbolic or oddly-encoded subset onto private codepoints,
    // and the browser then quietly falls back per character, so a probe run
    // that comes back the same width as the PDF's own advance is the proof that
    // the real glyphs were selected.
    const catalogue = {
        fontFamily: style.fontFamily || defaults.fontFamily,
        fontStack: null,
        fontLabel: null,
        bold,
        italic,
    };
    const embedded = style.embedded ? { ...catalogue, ...style.embedded } : null;
    // The catalogue family may be a web font nobody has fetched yet, and an
    // unloaded family measures as a fallback — which would calibrate the
    // replacement against the wrong metrics for good.
    await ensureFontsFor([{ ...catalogue, text: block.text }]);
    const embeddedRatio = embedded ? ratioFor(embedded) : null;
    const source = embeddedRatio && Math.abs(embeddedRatio - 1) <= 0.04 ? embedded : catalogue;
    const { fontFamily, fontStack, fontLabel, fontId, bold: cssBold, italic: cssItalic } = source;

    // Substitution differences sit within a few percent; anything wilder is a
    // spacing artefact, not a size error, so the clamp stays tight.
    //
    // The document's own face at the document's own size is exact by
    // definition, so when the probe agrees to within measuring noise keep the
    // PDF's size untouched — rounding it to a tenth, or nudging it by a
    // fraction of a percent, is visible on a long line.
    const ratio = source === embedded ? embeddedRatio : ratioFor(catalogue);
    const exact = source === embedded && Math.abs(ratio - 1) <= 0.01;
    const fontSize = exact
        ? block.fontSize
        : Math.max(5, Math.round(block.fontSize * Math.min(1.25, Math.max(0.8, ratio)) * 10) / 10);

    // Calibration gets close, never exact. Give the box whatever width the
    // widest source line actually needs at the final size, so a line that was
    // one line in the PDF can never come back wrapped in two.
    const widest = block.lines.reduce((max, l) => Math.max(
        max,
        measureLineWidth({ ...source, fontSize }, l.text),
    ), 0);
    // The residual after calibration is a couple of percent; the cap stops a
    // pathological font match from stretching the patch over whatever sits to
    // the right (a contents page's page numbers, say).
    let column = Math.min(Math.max(block.width, widest), block.width * 1.15 + 4);

    // Anchor to the original first baseline and left edge — the painter insets
    // by TEXT_PAD and drops the baseline 0.82em, so undo both here rather than
    // letting the block drift down and to the right.
    const pitch = block.lineHeight * block.fontSize;
    const x = block.x - TEXT_PAD;
    const y = block.lines[0].baseline - TEXT_PAD - fontSize * 0.82;

    // Sample from a fresh high-resolution render of just this paragraph: the
    // on-screen canvas is too coarse for small type, where every glyph pixel is
    // anti-aliased and black ink reads back as grey. Falling back to the page
    // canvas keeps this working if the render fails, and an unrendered canvas
    // is skipped entirely — its pixels are transparent black, which would paint
    // the patch black over the page.
    //
    // The probe reaches past the block on every side so the same render can
    // answer "how far does the ink actually go?" — see `measureInkBounds`.
    const SAMPLE_SCALE = 4;
    const maxProbeW = Math.max(24, pageW - Math.max(0, block.x - TEXT_PAD * 2));
    const maxProbeH = Math.max(12, pageH - Math.max(0, y - TEXT_PAD));
    const inkGap = Math.max(10, block.fontSize * 1.2);

    let canvas = null;
    let canvasScale = 0;
    let ink = null;
    let probeRect = clampRect({
        x: Math.max(0, block.x - TEXT_PAD * 2),
        y: Math.max(0, y - TEXT_PAD),
        w: Math.min(maxProbeW, block.width + TEXT_PAD * 4 + Math.max(72, block.width * 0.5)),
        h: Math.min(maxProbeH, block.height + TEXT_PAD * 4),
    });

    // The probe render queues behind whatever the viewer is already drawing. On
    // a document just opened that can be several seconds, and the paragraph the
    // user clicked would sit there doing nothing until it arrived. Sampling is
    // a refinement — the patch colour and the text colour both have sane
    // fallbacks — so it gets a deadline, not a promise.
    const probeRender = (rect) => Promise.race([
        renderRegionCanvas(pdf, pageNumber, rect, SAMPLE_SCALE),
        new Promise((_, reject) => { setTimeout(() => reject(new Error('probe-timeout')), PROBE_TIMEOUT_MS); }),
    ]);

    if (pdf && pageNumber) {
        // Widen until the ink stops before the probe's own edge. A fixed margin
        // is a guess about how wrong the reported width is, and when it guesses
        // low the measurement is clipped by the probe and the cover comes back
        // short — exactly the failure it exists to fix.
        for (let pass = 0; pass < 4; pass += 1) {
            try {
                canvas = await probeRender(probeRect);
                canvasScale = SAMPLE_SCALE;
                ink = measureInkBounds(canvas, SAMPLE_SCALE, { maxGap: inkGap });
            } catch {
                ink = null;
                break; /* fall back to the page canvas below */
            }
            if (!ink) break;

            const clippedRight = probeRect.w - ink.right < 2 && probeRect.w < maxProbeW;
            const clippedBottom = probeRect.h - ink.bottom < 2 && probeRect.h < maxProbeH;
            if (!clippedRight && !clippedBottom) break;
            probeRect = clampRect({
                ...probeRect,
                w: clippedRight ? Math.min(maxProbeW, probeRect.w * 1.6) : probeRect.w,
                h: clippedBottom ? Math.min(maxProbeH, probeRect.h + block.fontSize * 2) : probeRect.h,
            });
        }
    }

    // The scan reads anything darker than the page as ink and bridges blank
    // rows to hold a paragraph together. A watermark satisfies both: it is
    // darker than the page, and it runs down the whole of it — so the ink never
    // appears to end and the block grows until it has swallowed the paragraph
    // below. Keep the measurement inside the block it is measuring, give or
    // take a descender. The width has always been capped this way; the height
    // simply never was.
    if (ink) {
        const slack = Math.max(2, block.fontSize * 0.6);
        const top = Math.max(ink.top, block.y - slack - probeRect.y);
        const bottom = Math.min(ink.bottom, block.y + block.height + slack - probeRect.y);
        const left = Math.max(ink.left, block.x - slack - probeRect.x);
        const right = Math.min(ink.right, block.x + block.width * 1.15 + 4 - probeRect.x);
        ink = (bottom > top && right > left) ? { top, bottom, left, right } : null;
    }

    // What pdf.js reports and what the page prints can disagree — a broken
    // `/Widths` table or a horizontally scaled run both report a paragraph
    // narrower than it appears, and a cover cut to that width leaves the end of
    // every source line poking out beside the replacement. The measured ink
    // wins whenever it is wider, cap included: it is observed, not a guess, and
    // the gutter rule in `measureInkBounds` is what keeps a neighbouring column
    // out.
    //
    // Not when the paragraph is set in its own font at its own size: then the
    // measured advance IS the printed width, and the ink only adds the
    // overhang of a slanted or swashed last glyph — widen the column by that
    // and a justified paragraph re-spreads a point wider than it was. (The
    // patch below still takes the ink, so nothing is left peeking out.)
    if (ink && !exact) {
        const inkRight = probeRect.x + ink.right;
        column = Math.max(column, inkRight - block.x + 1);
    }

    const rect = clampRect({
        x,
        y,
        w: Math.max(24, column + TEXT_PAD * 2),
        h: Math.max(fontSize, (ink ? Math.max(block.height, probeRect.y + ink.bottom - y) : block.height) + TEXT_PAD * 2),
    });

    // The probe is cropped to its own rect, so colours are sampled at the
    // paragraph's offset inside it.
    let sampleRect = canvas
        ? { x: rect.x - probeRect.x, y: rect.y - probeRect.y, w: rect.w, h: rect.h }
        : rect;
    if (!canvas && baseCanvas) {
        canvas = baseCanvas;
        canvasScale = canvas.width / pageW;
        sampleRect = rect;
    }
    const background = canvas ? sampleBackgroundColor(canvas, sampleRect, canvasScale) : '#FFFFFF';
    const color = canvas ? sampleTextColor(canvas, sampleRect, canvasScale) : defaults.color;

    // The paragraph must come back on the number of lines it went in on.
    // Calibration can only land within a few percent, and a size that runs even
    // slightly long turns three source lines into four — the box then grows
    // downward over whatever follows it. Shrink until it fits, within a range
    // too small to read as a restyle.
    const linesAt = (size) => wrapTextLines({
        fontFamily,
        fontStack,
        fontSize: size,
        bold: cssBold,
        italic: cssItalic,
        w: rect.w,
        text: block.text,
        marks: null,
    }).length;
    let fitSize = fontSize;
    if (block.lines.length > 1 && !exact) {
        for (let i = 0; i < 8 && linesAt(fitSize) > block.lines.length; i += 1) {
            fitSize = Math.round(fitSize * 0.97 * 10) / 10;
            if (fitSize <= fontSize * 0.85) break;
        }
    }

    const box = makeText(
        rect,
        {
            ...defaults,
            fontFamily,
            fontStack,
            fontLabel,
            fontId,
            bold: cssBold,
            italic: cssItalic,
            fontSize: fitSize,
            // Keep the absolute line pitch of the source now that the size has
            // been recalibrated.
            lineHeight: Math.max(1, pitch / fitSize),
            align: block.align,
            color,
            // The paragraph underneath is covered by its own patch (below) and
            // deleted outright on export, so the box carries no fill of its own
            // — it can then be moved or resized without dragging a white
            // rectangle across the page.
            background: 'transparent',
        },
        block.text,
    );
    box.h = Math.max(rect.h, measuredTextHeight(box));
    // The region the source paragraph occupies, measured from the ink where the
    // render allowed it. The exporter deletes the text operators inside it, so
    // the finished PDF carries the replacement only — nothing hidden under the
    // patch to select, copy or search, and nothing left to peek out beside it.
    box.replaces = {
        x: Math.min(block.x, ink ? probeRect.x + ink.left : block.x) - 1,
        y: Math.min(block.y, ink ? probeRect.y + ink.top : block.y) - 1,
        w: Math.max(block.width, ink ? ink.right - ink.left : block.width) + 2,
        h: Math.max(block.height, ink ? ink.bottom - ink.top : block.height) + 2,
    };
    // The patch goes first, so it sits under the replacement. It is tied to the
    // box (`patchFor`), which is what removes it again if the box is deleted.
    const patch = makeWhiteout({ ...box.replaces }, background);
    patch.patchFor = box.id;

    return { box, patch };
}
